import React from "react";

import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import { Modal, ModalHeader, ModalTitle, ModalBody, ModalFooter } from "@common/components/modal";
import Dropdown from "@common/containers/fields/Dropdown";
import { MessageBox } from "@common/components/MessageBox";
import { getTestAttributes } from "@common/utils/tests";
import { trDts } from "@modules/datasheets/utils/i18n";

import { DatasheetSettingsConfigureTableProps } from "./configure-table.types";
import { useController } from "./configure-table.controller";

import "./configure-table.styles.scss";
import { FormatName } from "./components/format-name";

export const ConfigureTable: React.FC<DatasheetSettingsConfigureTableProps> = (props) => {
  const { onClose, dropdownFormats, title, checkFormatNameExists } = props;

  const {
    isAllowedToSubmit,
    handleSubmit,
    configureTableSubmitting,
    formatTablesHeader,
    setSelectedFormatId,
    selectedFormatId,
    closeMessage,
    messageProps,
    internalTableFormats,
    openFormatName,
    formatNameOpened,
    closeFormatName,
  } = useController(props);

  return (
    <Modal
      show
      backdrop="static"
      bsSize="md"
      bsClass="bootstrap modal"
      className="DatasheetElementSettingsConfigureTable"
      dialogClassName="DatasheetElementSettingsConfigureTable__dialog"
      animation={false}
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle {...getTestAttributes({ id: "fp-table-configuration-wnd-1" })}>{title}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        {formatNameOpened && (
          <FormatName
            onSubmit={handleSubmit}
            onClose={closeFormatName}
            checkFormatNameExists={checkFormatNameExists}
            formats={[]}
          />
        )}
        <MessageBox hideMessageBox={closeMessage} {...messageProps} />
        <div className="DatasheetElementSettingsConfigureTable__dropdown__inner">
          <Dropdown
            {...getTestAttributes({ id: "table-configuration-wnd-format-dd-drop-btn" })}
            options={[
              { value: "", label: trDts("None") },
              ...dropdownFormats.map((format) => ({ value: format.id, label: format.name })),
            ]}
            selectedValue={selectedFormatId ?? ""}
            onValueChanged={setSelectedFormatId}
          />
        </div>
        <div
          {...getTestAttributes({ id: "table-configuration-wnd-format-grid" })}
          className="DatasheetElementSettingsConfigureTable__formats-table"
        >
          <Flex width="100%" className="DatasheetElementSettingsConfigureTable__formats-table__header">
            {formatTablesHeader.map((format, formatIndex) => (
              <div
                {...getTestAttributes({ id: `table-configuration-wnd-format-grid-0-${formatIndex}` })}
                key={format.title}
                style={format.style}
                className="DatasheetElementSettingsConfigureTable__formats-table__header__column"
              >
                {format.title}
              </div>
            ))}
          </Flex>
          {internalTableFormats.map((item) => (
            <Flex key={item.id} width="100%" style={{ marginTop: "8px" }}>
              {formatTablesHeader.map((format, formatIndex) => (
                <Flex
                  {...getTestAttributes({ id: `table-configuration-wnd-format-grid-1-${formatIndex}` })}
                  key={`${item.id}-${format.title}`}
                  className="DatasheetElementSettingsConfigureTable__formats-table__body__column"
                  alignItems="center"
                >
                  {format.component({ item })}
                </Flex>
              ))}
            </Flex>
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Flex justify="flex-end">
          <Button {...getTestAttributes({ id: "btn-add-tab-wnd-cancel" })} onClick={onClose}>
            {trDts("Cancel")}
          </Button>
          <Button
            {...getTestAttributes({ id: "btn-add-tab-wnd-create" })}
            disabled={!isAllowedToSubmit}
            bsStyle="primary"
            onClick={openFormatName}
          >
            {configureTableSubmitting ? trDts("Loading") : trDts("Save format")}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
